// ONNX Runtime 单例管理器
class ONNXModelManager {
    constructor() {
        this.ortInstance = null;
        this.session = null;
        this.isLoading = false;
        this.loadPromise = null;
        this.currentProvider = null;
        
        // 立即开始预加载模型文件到浏览器缓存
        this.preloadModel();
    }

    // 获取 ONNX Runtime（使用全局对象）
    async getOnnxRuntime() {
        if (!this.ortInstance) {
            // 等待全局 ort 对象可用
            if (typeof window !== 'undefined' && window.ort) {
                this.ortInstance = window.ort;
            } else {
                // 如果全局对象还没加载，等待一下
                await new Promise((resolve, reject) => {
                    let attempts = 0;
                    const maxAttempts = 50; // 最多等待5秒
                    const checkOrt = () => {
                        if (typeof window !== 'undefined' && window.ort) {
                            this.ortInstance = window.ort;
                            resolve();
                        } else if (attempts < maxAttempts) {
                            attempts++;
                            setTimeout(checkOrt, 100);
                        } else {
                            reject(new Error('ONNX Runtime 全局对象加载超时'));
                        }
                    };
                    checkOrt();
                });
            }
        }
        return this.ortInstance;
    }

    // 获取模型会话（单例模式）
    async getSession() {
        if (this.session) {
            return this.session;
        }

        if (this.isLoading) {
            return this.loadPromise;
        }

        this.isLoading = true;
        this.loadPromise = this.loadModel();

        try {
            await this.loadPromise;
            return this.session;
        } finally {
            this.isLoading = false;
            this.loadPromise = null;
        }
    }

    // 预加载模型文件到浏览器缓存
    async preloadModel() {
        const MODEL_URL = 'model/L2CSNet_gaze360.onnx';
        try {
            const response = await fetch(MODEL_URL, {
                method: 'HEAD', // 只获取头部信息，检查文件是否存在
                cache: 'force-cache' // 强制使用缓存
            });
            if (!response.ok) {
                console.warn('模型文件不存在或无法访问:', MODEL_URL);
            }
        } catch (error) {
            console.warn('预加载模型失败:', error);
        }
    }

    // 加载模型（内部方法）
    async loadModel() {
        const MODEL_URL = 'model/L2CSNet_gaze360.onnx';
        const providers = ['webgpu', 'webgl', 'wasm', 'cpu'];
        let lastError = null;

        const ort = await this.getOnnxRuntime();

        // 使用优化的 fetch 选项来确保缓存
        const fetchOptions = {
            cache: 'force-cache', // 优先使用缓存
            headers: {
                'Cache-Control': 'max-age=31536000' // 建议缓存1年
            }
        };

        for (const ep of providers) {
            try {
                // 优化ONNX Runtime配置
                const options = {
                    executionProviders: [ep],
                    graphOptimizationLevel: 'all', // 启用所有图优化
                    executionMode: 'sequential', // 顺序执行模式
                    enableCpuMemArena: true, // 启用CPU内存池
                    enableMemPattern: true, // 启用内存模式优化
                    ...(ep === 'webgpu' && {
                        // WebGPU特定优化
                        webgpu: {
                            preferredLayout: 'NHWC'
                        }
                    }),
                    ...(ep === 'webgl' && {
                        // WebGL特定优化
                        webgl: {
                            contextId: 'webgl2',
                            matmulMaxBatchSize: 16,
                            textureCacheMode: 'full'
                        }
                    })
                };

                // 确保 InferenceSession 存在
                if (!ort.InferenceSession) {
                    throw new Error('ONNX Runtime InferenceSession 不可用');
                }

                // 使用带缓存优化的选项创建会话
                this.session = await ort.InferenceSession.create(MODEL_URL, {
                    ...options,
                    // 传递 fetch 选项以优化缓存
                    env: {
                        ...ort.env,
                        fetchInit: fetchOptions
                    }
                });
                this.currentProvider = ep;

                console.log(`模型加载完成，后端: ${ep}`);

                // 触发模型加载完成事件
                this.onModelLoaded?.(ep);

                return this.session;
            } catch (e) {
                lastError = e;
                console.warn(`提供者 ${ep} 加载失败:`, e);
            }
        }

        const error = new Error('模型加载失败: ' + lastError?.message);
        this.onModelError?.(error);
        throw error;
    }
    async processFace(faceImgData) {
        // 确保模型会话已经准备好
        const session = await this.getSession();
        const ort = await this.getOnnxRuntime();
        
        let input = this.preprocessFace(faceImgData);
        const tensor = new ort.Tensor('float32', input, [1, 3, 448, 448]);
        const feeds = { input: tensor };
        
        // 模型推理
        const results = await session.run(feeds);
        let yaw_logits = results.yaw.cpuData;
        let pitch_logits = results.pitch.cpuData;
        let yaw = this.getAngleFromBins(yaw_logits) * Math.PI / 180;
        let pitch = this.getAngleFromBins(pitch_logits) * Math.PI / 180;
        return { yaw, pitch };
    }


    preprocessFace(faceImgData224) {    
        const mean = [0.485, 0.456, 0.406];
        const std = [0.229, 0.224, 0.225];
        const invStd = [1 / std[0], 1 / std[1], 1 / std[2]]; // 预计算除法为乘法
        const scale255 = 1 / 255; // 预计算1/255
        let data224 = faceImgData224.data;
        let float32 = new Float32Array(3 * 448 * 448);

        // 使用最近邻插值，速度更快
        const scale = 448 / 224;
        for (let y = 0; y < 448; y++) {
            for (let x = 0; x < 448; x++) {
                // 最近邻插值 - 直接取最近的像素
                let srcX = Math.round(x / scale);
                let srcY = Math.round(y / scale);

                // 确保不越界
                srcX = Math.min(srcX, 223);
                srcY = Math.min(srcY, 223);

                let idx = (srcY * 224 + srcX) * 4;
                let i = y * 448 + x;

                // 使用预计算的参数优化归一化
                float32[0 * 448 * 448 + i] = (data224[idx] * scale255 - mean[0]) * invStd[0];
                float32[1 * 448 * 448 + i] = (data224[idx + 1] * scale255 - mean[1]) * invStd[1];
                float32[2 * 448 * 448 + i] = (data224[idx + 2] * scale255 - mean[2]) * invStd[2];
            }
        }
        return float32;
    }

    // 优化的softmax计算
    softmax(arr) {
        const max = Math.max(...arr);
        let sum = 0;
        const exps = new Float32Array(arr.length);

        // 第一遍：计算exp和sum
        for (let i = 0; i < arr.length; i++) {
            exps[i] = Math.exp(arr[i] - max);
            sum += exps[i];
        }

        // 第二遍：归一化
        const invSum = 1 / sum;
        for (let i = 0; i < arr.length; i++) {
            exps[i] *= invSum;
        }

        return exps;
    }
    // 优化的角度计算
    getAngleFromBins(logits) {
        const bins = 90;
        const bin_width = 4;
        const probs = this.softmax(logits);
        let angle = 0;
        for (let i = 0; i < bins; i++) {
            angle += probs[i] * i; // 直接使用i，避免idx_tensor查找
        }
        return angle * bin_width - 180;
    }


    // 获取当前使用的执行提供者
    getCurrentProvider() {
        return this.currentProvider;
    }

    // 检查是否正在加载
    isModelLoading() {
        return this.isLoading;
    }

    // 设置回调函数
    setCallbacks(onLoaded, onError) {
        this.onModelLoaded = onLoaded;
        this.onModelError = onError;
    }

    // 重置管理器（用于重新加载）
    reset() {
        this.session = null;
        this.currentProvider = null;
        this.isLoading = false;
        this.loadPromise = null;
    }
}

// 创建全局单例实例
const modelManager = new ONNXModelManager();

// 导出便捷函数
export async function loadModel() {
    return await modelManager.getSession();
}
export async function processFace(faceImgData) {
    return await modelManager.processFace(faceImgData);
}
export async function getModelSession() {
    return await modelManager.getSession();
}

export function getCurrentProvider() {
    return modelManager.getCurrentProvider();
}

export function setModelCallbacks(onLoaded, onError) {
    modelManager.setCallbacks(onLoaded, onError);
}

export function isModelLoading() {
    return modelManager.isModelLoading();
}

export function resetModel() {
    modelManager.reset();
}
